import { AppState } from '../store/model';

import {
  AirFareOption,
  AirSegmentDetails,
  CarOption,
  CarSegmentDetails,
  CarType,
  FlightOption,
  HotelOption,
  HotelRateOption,
  HotelSegmentDetails,
  RateTypes,
} from '../models/itinerary';

export const getRates = (
  state: AppState,
  type: string,
  segmentId: number,
  optionId: string,
): RateTypes[] => {
  switch (type) {
    case 'Air':
      const airSegmentDetails = state.segmentOptions[segmentId].options[
        optionId
      ] as AirSegmentDetails;

      return airSegmentDetails.fareOptions || [];

    case 'Hotel':
      const hotelSegmentDetails = state.segmentOptions[segmentId].options[
        optionId
      ] as HotelSegmentDetails;

      return hotelSegmentDetails.rates || [];

    case 'Car':
      const carSegmentDetails = state.segmentOptions[segmentId].options[
        optionId
      ] as CarSegmentDetails;

      return carSegmentDetails.carTypes || [];
  }
  return [];
};

export const getSelectedRate = (
  state: AppState,
  type: string,
  segmentId: number,
  optionId: string,
  returnDefaultFare: boolean,
) => {
  const rates = getRates(state, type, segmentId, optionId);

  switch (type) {
    case 'Air':
      const fareOptions = rates as AirFareOption[];

      const selectedAirFareId =
        (state.selectedRate.rate &&
          (state.selectedRate.rate as FlightOption).fareId) ||
        state.selectedOptions[segmentId].airSegment!.fareId;

      let airFareOption = fareOptions.find(
        (option) => option.fareId === selectedAirFareId,
      );

      if (!airFareOption && returnDefaultFare) {
        airFareOption = fareOptions.sort(
          (a, b) => a.totalCost - b.totalCost,
        )[0];
      }

      return airFareOption;

    case 'Hotel':
      const hotelRates = rates as HotelRateOption[];

      const selectedHotelRateId =
        (state.selectedRate.rate &&
          (state.selectedRate.rate as HotelOption).rateId) ||
        state.selectedOptions[segmentId].hotelSegment!.rateId;

      let hotelRateOption = hotelRates.find(
        (option) => option.rateId === selectedHotelRateId,
      )!;

      if (!hotelRateOption && returnDefaultFare) {
        hotelRateOption = hotelRates.sort((a, b) => a.cost - b.cost)[0];
      }

      return hotelRateOption;

    case 'Car':
      const carTypes = rates as CarType[];

      const selectedCarTypeId =
        (state.selectedRate.rate &&
          (state.selectedRate.rate as CarOption).carId) ||
        state.selectedOptions[segmentId].carSegment!.carId;

      let carOption = carTypes.find(
        (option) => option.carId === selectedCarTypeId,
      )!;

      if (!carOption && returnDefaultFare) {
        // Find check this car when no car is found.
        carOption = carTypes.sort((a, b) => a.totalAmount - b.totalAmount)[0];
      }
      return carOption;
  }
};

export const getRateText = (type: string, rate: RateTypes) => {
  if (!rate) return '';

  switch (type) {
    case 'Air':
      const airFareOption = rate as AirFareOption;
      return `${airFareOption.description} (${airFareOption.class})`;

    case 'Hotel':
      const hotelRateOption = rate as HotelRateOption;
      return (
        hotelRateOption.longDescription || hotelRateOption.shortDescription
      );

    case 'Car':
      const carOption = rate as CarType;
      return `${carOption.carClass} ${carOption.category} ${
        carOption.carModel.match(/[Aa]ny/) ? '' : carOption.carModel
      }`;
  }
};

export const getRatePrice = (
  type: string,
  rate: RateTypes,
  duration?: number,
) => {
  if (!rate) return 0;

  switch (type) {
    case 'Air':
      return (rate as AirFareOption).totalCost;
    case 'Hotel':
      return (rate as HotelRateOption).cost * duration!;
    case 'Car':
      return (rate as CarType).totalAmount!;
  }
};

export const getRateCurrencyCode = (type: string, rate: RateTypes) => {
  if (!rate) return '';

  switch (type) {
    case 'Air':
      return (rate as AirFareOption).currencyCode;
    case 'Hotel':
      return (rate as HotelRateOption).currency.code;
    case 'Car':
      return (rate as CarType).currency.code;
  }
};
